import { useEffect } from "react"
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import {
  Button,
  DebouncedInput,
  ExportButton,
  Select,
} from "~/ui-rtk/components/ui/controls"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { toCurrencyWithCommas } from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { isSafeHttpUrl } from "~/ui-rtk/utils/validators"
import useConnect, {
  CHANNEL_OPTIONS,
  RANGE_OPTIONS,
  STATUS_OPTIONS,
} from "./connect"

const fmtCtr = (ctr: number | null) =>
  ctr == null ? "—" : `${ctr.toFixed(2)}%`

export default function AdDestinations() {
  const {
    ads,
    isFetching,
    isError,
    refetch,
    range,
    setRange,
    status,
    setStatus,
    channel,
    setChannel,
    search,
    setSearch,
    exportCsv,
    hasCompany,
  } = useConnect()

  useEffect(() => {
    document.title = "Marathon Data: Paid Media > Ad Destinations"
  }, [])

  return (
    <div className="min-h-screen">
      <div className="text-start mb-6">
        <Breadcrumbs
          items={[
            {
              slug: brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION,
              name: "Paid Media",
            },
            { slug: "ad-destinations", name: "Ad Destinations" },
          ]}
        />
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">Ad Destinations</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-system-gray-400">
          One row per ad with the page its click lands on, the URL tags the
          platform appends, and spend, impressions, clicks and CTR for the
          period. Use it after a site change to confirm every ad still points at
          the right page, then export the list to hand it off.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="w-40">
          <Select
            options={RANGE_OPTIONS}
            value={range}
            onChange={e => setRange(e.target.value)}
          />
        </div>
        <div className="w-36">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={e => setStatus(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            options={CHANNEL_OPTIONS}
            value={channel}
            onChange={e => setChannel(e.target.value)}
          />
        </div>
        <DebouncedInput
          width={260}
          placeholder="Ad name contains…"
          defaultValue={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button
          type="button"
          size="sm"
          variant={{ variant: "text" }}
          onClick={() => refetch()}
          disabled={isFetching || !hasCompany}
        >
          <ArrowPathIcon
            className={cn("h-4 w-4", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
        <ExportButton onExport={exportCsv} disabled={ads.length === 0} />
      </div>

      {isError && (
        <div className="rounded-xl border border-system-error-500/40 bg-system-error-500/10 px-5 py-4 text-sm text-system-error-300">
          The ad list could not be loaded. If this company has no paid media
          snapshot yet, there is nothing to show.
        </div>
      )}

      {isFetching && ads.length === 0 && (
        <div className="h-96 animate-pulse rounded-xl border border-system-blue-600/40 bg-system-blue-800/60" />
      )}

      {(!isFetching || ads.length > 0) && !isError && (
        <section className="animate-fadeIn">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-6 w-1 rounded-full bg-system-brand-pink-500" />
            <h2 className="text-xl font-semibold text-white">Ads</h2>
            <span className="ml-1 rounded-full bg-system-blue-700/60 px-2 py-0.5 text-xs font-medium text-system-gray-300">
              {ads.length}
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-system-blue-600/40 bg-system-blue-800/40">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-system-blue-600/40 bg-system-blue-800/80 text-xs uppercase tracking-wider text-system-gray-400">
                    <th className="px-5 py-3 font-semibold">Ad</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Destination</th>
                    <th className="px-5 py-3 text-right font-semibold">
                      Spend
                    </th>
                    <th className="px-5 py-3 text-right font-semibold">Impr</th>
                    <th className="px-5 py-3 text-right font-semibold">
                      Clicks
                    </th>
                    <th className="px-5 py-3 text-right font-semibold">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map(ad => (
                    <tr
                      key={`${ad.channel}-${ad.adId}`}
                      className="border-b border-system-blue-600/20 transition-colors last:border-b-0 hover:bg-system-blue-700/30"
                    >
                      <td className="max-w-xs px-5 py-4 align-top">
                        <p
                          className="truncate font-medium text-white"
                          title={ad.adName}
                        >
                          {ad.adName || ad.adId}
                        </p>
                        <p
                          className="mt-0.5 truncate text-xs text-system-gray-500"
                          title={`${ad.campaignName} · ${ad.channel} · ${ad.adId}`}
                        >
                          {ad.campaignName} · {ad.channel} · {ad.adId}
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top font-mono text-xs text-system-gray-300">
                        {ad.status}
                      </td>
                      <td className="max-w-md px-5 py-4 align-top">
                        {!ad.url && (
                          <span className="font-mono text-xs text-system-gray-500">
                            (none reported — catalog/dynamic ad)
                          </span>
                        )}
                        {/* Only http(s) becomes a link: `ad.url` is advertiser
                            data from the ad platforms, and a `javascript:` value
                            would run in this origin on click. It is still shown
                            so a bad value can be seen and fixed. */}
                        {ad.url && isSafeHttpUrl(ad.url) && (
                          <a
                            href={ad.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex max-w-full items-center gap-1 truncate font-mono text-xs text-system-gray-300 hover:text-white"
                            title={ad.url}
                          >
                            <span className="truncate">{ad.url}</span>
                            <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
                          </a>
                        )}
                        {ad.url && !isSafeHttpUrl(ad.url) && (
                          <span
                            className="block truncate font-mono text-xs text-system-error-300"
                            title={ad.url}
                          >
                            {ad.url}
                          </span>
                        )}
                        <p
                          className="mt-1 truncate font-mono text-[11px] text-system-gray-500"
                          title={ad.urlTags}
                        >
                          {ad.hasUrlTags ? ad.urlTags : "no url_tags"}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right align-top tabular-nums text-white">
                        {toCurrencyWithCommas(ad.spend)}
                      </td>
                      <td className="px-5 py-4 text-right align-top tabular-nums text-white">
                        {ad.impressions.toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-right align-top tabular-nums text-white">
                        {ad.clicks.toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-right align-top tabular-nums text-white">
                        {fmtCtr(ad.ctr)}
                      </td>
                    </tr>
                  ))}
                  {ads.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-10 text-center text-sm text-system-gray-400"
                      >
                        No ads match these filters in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
